import React from "react";
import { useLocation } from "react-router-dom";
import SEO from "@/components/SEO";
import usePageContent, { s } from "@/hooks/usePageContent";
import { parseLocaleFromPath } from "@/lib/locale";
import { Quote } from "lucide-react";
import RichContent from "@/components/RichContent";

// Localised alt text for the interim workshop scene image (same asset as the homepage hero).
const STORY_IMAGE_ALT = {
  hr: "Radionica Tambure Žmegač u Varaždinu",
  en: "Tambure Žmegač workshop in Varaždin",
  de: "Werkstatt Tambure Žmegač in Varaždin",
  fr: "Atelier Tambure Žmegač à Varaždin",
};
const STORY_IMAGE_SRC = "/seed/workshop_mainpage_hero.webp";

export default function Story() {
  const { locale } = parseLocaleFromPath(useLocation().pathname);
  const { data: page } = usePageContent("story", locale);
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <>
      <SEO
        title={page?.meta_title}
        description={page?.meta_description}
        image={`${origin}${STORY_IMAGE_SRC}`}
      />
      <article className="bg-background grain-bg">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-[2fr_3fr] gap-12 lg:gap-16">
          <div className="lg:sticky lg:top-24 lg:self-start">
            <div className="text-[11px] uppercase tracking-[0.3em] text-primary mb-4">{s(page, "eyebrow")}</div>
            <h1 className="font-serif text-5xl lg:text-6xl text-foreground leading-[1.02]">
              {s(page, "title")}
            </h1>
            <p className="mt-4 text-base text-muted-foreground italic">{s(page, "subtitle")}</p>
            <div className="mt-8 wood-divider w-24" />
            {/* TODO: user to upload real Mario Žmegač portrait via /admin/gallery, then wire URL here */}
            <img
              src={STORY_IMAGE_SRC}
              alt={STORY_IMAGE_ALT[locale] || STORY_IMAGE_ALT.hr}
              loading="lazy"
              data-testid="story-image"
              className="mt-10 rounded-lg shadow-ambient w-full h-auto object-cover aspect-[4/3]"
            />
          </div>

          <div className="prose-tz max-w-2xl space-y-6 text-foreground/85 text-lg leading-relaxed" data-testid="story-body">
            <RichContent
              html={s(page, "p1")}
              className="text-xl first-letter:font-serif first-letter:text-5xl first-letter:float-left first-letter:mr-3 first-letter:leading-none first-letter:text-primary"
            />
            <RichContent html={s(page, "p2")} />
            <RichContent html={s(page, "p3")} />

            <blockquote className="my-12 border-l-4 border-primary pl-6 py-2 not-italic">
              <Quote className="h-6 w-6 text-primary mb-3" strokeWidth={1} />
              <div className="font-serif text-2xl italic text-foreground leading-snug">
                &ldquo;{s(page, "quote")}&rdquo;
              </div>
              <div className="mt-3 text-xs uppercase tracking-[0.25em] text-muted-foreground">
                — {s(page, "quote_attrib")}
              </div>
            </blockquote>

            <RichContent html={s(page, "p4")} />
            <RichContent html={s(page, "p5")} />
          </div>
        </div>
      </article>
    </>
  );
}
